import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Eligibility, TemplateFeature } from '../types'
import { createTemplate, deleteTemplate, getFeatures, getFeaturesForTemplate, getTemplate, getTemplates, updateTemplate } from './endpoints'

enum queryKeys {
  SUBSCRIPTION = 'subscription',
  TEMPLATES = 'templates',
  FEATURES = 'features',
}

export const useGetTemplates = () =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES],
    queryFn: getTemplates,
  })

export const useGetTemplate = (id: string) =>
  useQuery({
    queryKey: ['template', 'features', id],
    queryFn: () => getTemplate(id),
  })

export const useCreateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, externalName, eligibility }: { name: string; externalName: string; eligibility: Eligibility }) =>
      createTemplate(name, externalName, eligibility),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES],
      })
    },
  })
}

export const useGetFeaturesForTemplate = (templateId: string) =>
  useQuery({
    queryKey: [],
    queryFn: async () => getFeaturesForTemplate(templateId),
  })

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES],
      })
    },
  })
}

export const useUpdateTemplate = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (features: TemplateFeature[]) => updateTemplate(templateId, features),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES],
      })
    },
  })
}

export const useGetFeatures = () =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES],
    queryFn: getFeatures,
  })
